import { useState, useCallback } from 'react'
import { useSelector, useDispatch } from 'react-redux'
import { selectAuth } from '../../services/auth/selectors'
import { setAiGa4Event } from '../../services/event-tracking/actions'
import { AI_RESPONSE_FEEDBACK_GA4_EVENT } from '../../services/event-tracking/index'
import { useUpdateMessagePairThumbsMutation } from './queries.generated'
import type { OracleThumbValue } from './OracleThumbs'

interface UseThumbsUpdateOptions {
  /**
   * Optional error handler called when mutation fails.
   * Receives the error for display purposes (e.g., showing a toast).
   * Note: UI state is automatically rolled back on error.
   */
  onError?: (error: unknown) => void
}

interface UseThumbsUpdateResult {
  /**
   * Update the thumbs value for a message pair. Handles toggle logic:
   * - Clicking the same value clears it to 'none'
   * - Clicking a different value sets the new value
   *
   * On mutation failure, the UI state automatically rolls back to the previous value.
   */
  updateThumb: (
    messagePairId: string,
    newValue: OracleThumbValue,
    currentBackendValue?: OracleThumbValue
  ) => Promise<void>
  /**
   * Get the current thumbs value for a message pair, preferring local state
   * over backend value
   */
  getThumbValue: (messagePairId: string, backendValue?: OracleThumbValue) => OracleThumbValue
  /**
   * The current local thumbs values state
   */
  thumbsValues: Record<string, OracleThumbValue>
  /**
   * Whether a mutation is currently in progress
   */
  isUpdating: boolean
}

/**
 * Hook for managing thumbs up/down state and mutations.
 * Handles:
 * - Optimistic UI updates with automatic rollback on error
 * - GraphQL mutation
 * - GA4 analytics tracking
 * - Toggle behavior (clicking same thumb clears it)
 */
const useThumbsUpdate = (options?: UseThumbsUpdateOptions): UseThumbsUpdateResult => {
  const auth = useSelector(selectAuth)
  const dispatch = useDispatch()
  const [updateMutation, { loading: isUpdating }] = useUpdateMessagePairThumbsMutation()
  const [thumbsValues, setThumbsValues] = useState<Record<string, OracleThumbValue>>({})

  const updateThumb = useCallback(async (
    messagePairId: string,
    newValue: OracleThumbValue,
    currentBackendValue: OracleThumbValue = 'none',
  ) => {
    const localValue = thumbsValues[messagePairId]
    const currentValue = localValue !== undefined ? localValue : currentBackendValue

    // Early exit if clicking 'none' when already 'none'
    if (currentValue === 'none' && newValue === 'none') {
      return
    }

    // Toggle logic: clicking same value clears to 'none'
    const valueToSend: OracleThumbValue = currentValue === newValue ? 'none' : newValue

    // Optimistic update
    setThumbsValues((prev) => ({
      ...prev,
      [messagePairId]: valueToSend,
    }))

    try {
      await updateMutation({
        variables: {
          id: messagePairId,
          input: { thumbsValue: valueToSend },
        },
        context: {
          jwt: auth.get('jwt'),
          useMaster: true,
        },
      })

      // GA4 analytics: track when setting a value (not when clearing)
      if (valueToSend !== 'none') {
        dispatch(
          setAiGa4Event(
            AI_RESPONSE_FEEDBACK_GA4_EVENT.mergeDeep({
              content_rating: valueToSend,
            }),
          ),
        )
      }
    } catch (error) {
      // Rollback to previous value on failure
      setThumbsValues((prev) => ({
        ...prev,
        [messagePairId]: currentValue,
      }))

      if (options?.onError) {
        options.onError(error)
      }
    }
  }, [thumbsValues, updateMutation, auth, dispatch, options])

  const getThumbValue = useCallback((
    messagePairId: string,
    backendValue: OracleThumbValue = 'none',
  ): OracleThumbValue => {
    const localValue = thumbsValues[messagePairId]
    return localValue !== undefined ? localValue : backendValue
  }, [thumbsValues])

  return {
    updateThumb,
    getThumbValue,
    thumbsValues,
    isUpdating,
  }
}

export default useThumbsUpdate
